import type * as React from 'react';
import { cn } from './cn';

/**
 * Card primitives — the surface most of the product's content sits on.
 *
 * `Card` owns the vertical rhythm (gap and padding), so the parts inside only
 * set horizontal padding. That is what keeps every card in the app spaced
 * identically without each page re-deciding.
 *
 * The spacing is the dense one. shadcn's defaults — 24px of padding and a 24px
 * gap between parts — are drawn for a marketing page with three cards on it;
 * this product puts five metrics, a map and a forty-row table on one screen,
 * and at 24px the chrome was taking more room than the numbers. 12px padding
 * with a 10px gap is what the instrument products use, and it is what lets the
 * fleet summary, the map and the first table rows share a laptop screen.
 *
 * A card that genuinely wants air — an empty state, a sign-in panel — asks for
 * it at the call site with `p-6`, which is the rarer case and now reads as a
 * deliberate one.
 *
 * `shadow-xs` is the resting level of the elevation scale, not Tailwind's
 * fixed `rgb(0 0 0 / 0.05)` — see `@routly/tokens/elevation.css`. The default
 * was black on a near-black page, so in dark mode the card had a border and
 * nothing else, and the two themes were not the same design.
 *
 * `interactive` is for a card that is itself a link or a button. It is the one
 * place this product lifts anything on hover, and it does so because a card
 * that navigates has to say so before it is clicked; a card that merely
 * contains a table must not.
 */

function Card({
  className,
  interactive = false,
  ...props
}: React.ComponentProps<'div'> & { interactive?: boolean }) {
  return (
    <div
      data-slot="card"
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-border bg-card py-4 text-card-foreground shadow-xs',
        interactive &&
          'cursor-pointer transition-[box-shadow,border-color,transform] duration-150 ' +
            'hover:-translate-y-px hover:shadow-md focus-within:shadow-md',
        className
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'grid auto-rows-min items-center gap-0.5 px-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-3',
        className
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('text-lg font-semibold leading-snug', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}

/** Sits opposite the title — a button or menu in the card's top-right. */
function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-content" className={cn('px-4', className)} {...props} />;
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-4 [.border-t]:pt-3', className)}
      {...props}
    />
  );
}

export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
